import React, { useState } from 'react';
import { authFetch } from '../auth';
import './ShareSheet.css';

export default function ShareSheet({ text, selections, onClose, onCreateNote, onCreateTask }) {
  const [showLink, setShowLink] = useState(false);
  const [expires, setExpires] = useState(30);
  const [neverConfirm, setNeverConfirm] = useState(false);
  const [showName, setShowName] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);

  const handleCopy = async () => {
    var plain = String(text || '').replace(/^#{1,6}\s+/gm, '').replace(/(\*\*|__)(.*?)\1/g, '$2').replace(/(\*|_)(.*?)\1/g, '$2').replace(/`{1,3}.*?`{1,3}/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim();
    var html = String(text || '').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\*(.*?)\*/g, '<i>$1</i>').replace(/`{1,3}(.*?)`{1,3}/g, '<code>$1</code>').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>').replace(/\n/g, '<br>');
    if (window.ClipboardItem && navigator.clipboard.write) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/plain': new Blob([plain], {type: 'text/plain'}), 'text/html': new Blob(['<html><body>' + html + '</body></html>'], {type: 'text/html'}) })
        ]);
      } catch(e) { await navigator.clipboard.writeText(plain); }
    } else { await navigator.clipboard.writeText(plain); }
    onClose();
  };

  const canCreate = ((selections && selections.length > 0) || (text && text.trim())) && (expires !== 'never' || neverConfirm);

  const handleCreateLink = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await authFetch('/api/chat-shares', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Text-only shares (e.g. Notes) send NO selections so the server takes its
          // text-only path. The old fake { sessionId:'notes' } selection forced the
          // chat-session ownership check, which always failed → "session not owned by user".
          selections: (selections && selections.length > 0) ? selections : [],
          text: text || '',
          visibility: 'public',
          expiresInDays: expires === 'never' ? null : expires,
          showSharerName: showName
        })
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      setCreated(j);
    } catch(e) {
      setError(e.message || 'Failed');
    } finally {
      setCreating(false);
    }
  };

  const copyCreatedLink = async () => {
    if (!created || !created.url) return;
    try { await navigator.clipboard.writeText(created.url); } catch(e) { window.prompt('Copy this link:', created.url); }
  };

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-sheet" onClick={e => e.stopPropagation()}>
        <div className="share-handle" />
        <h3 className="share-title">Share</h3>
        <div className="share-options">
          <button className="share-option" onClick={handleCopy}>
            <span className="share-icon copy">📋</span>
            <span className="share-label">Copy text</span>
          </button>
          <button className="share-option" onClick={() => { onCreateNote(text); onClose(); }}>
            <span className="share-icon note">📝</span>
            <span className="share-label">New Note</span>
          </button>
          <button className="share-option" onClick={() => { onCreateTask(text); onClose(); }}>
            <span className="share-icon task">✅</span>
            <span className="share-label">New Task</span>
          </button>
          <button className="share-option" onClick={() => setShowLink(v => !v)} disabled={(!selections || selections.length === 0) && !(text && text.trim())}>
            <span className="share-icon link">🔗</span>
            <span className="share-label">Web link</span>
          </button>
        </div>

        {showLink && (
          <div className="share-link-section">
            {!created ? (
              <>
                <div className="share-link-row">
                  <label>Expires</label>
                  <select value={expires} onChange={e => { var v = e.target.value === 'never' ? 'never' : parseInt(e.target.value, 10); setExpires(v); setNeverConfirm(false); }}>
                    <option value={1}>1 day</option>
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value="never">Never</option>
                  </select>
                </div>
                {expires === 'never' && (
                  <label className="share-link-confirm">
                    <input type="checkbox" checked={neverConfirm} onChange={e => setNeverConfirm(e.target.checked)} />
                    <span>I understand this link will never expire</span>
                  </label>
                )}
                <label className="share-link-confirm">
                  <input type="checkbox" checked={showName} onChange={e => setShowName(e.target.checked)} />
                  <span>Show my first name on the share</span>
                </label>
                <button className="share-link-create-btn" onClick={handleCreateLink} disabled={!canCreate || creating}>
                  {creating ? 'Creating…' : 'Create link'}
                </button>
                {error && <div className="share-link-error">{error}</div>}
              </>
            ) : (
              <>
                <div className="share-link-result">
                  <div className="share-link-url">{created.url}</div>
                  <div className="share-link-actions">
                    <button onClick={copyCreatedLink}>Copy link</button>
                    <button onClick={() => window.open(created.url, '_blank', 'noopener')}>Open</button>
                  </div>
                  {created.expiresAt && <div className="share-link-exp">Expires {new Date(created.expiresAt).toLocaleDateString()}</div>}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
